import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, Eye, Brain, Gauge, Play, Users, Rocket, Target, Trophy, BookOpen, TrendingUp, CheckCircle, ChevronDown, Ghost, Search, Scroll, Cpu, Palette, MessageCircle, Heart, Leaf, Briefcase, Plane, Stethoscope, Smile, Theater, History, Mountain, Globe, RefreshCw } from 'lucide-react';
import { calculateORP } from '../utils/rsvp';
import { TIER_PRICING, TIER_BENEFITS } from '../types/membership';
import { usePageMeta } from '../hooks/usePageMeta';

interface LandingPageProps {
  onStartTour?: () => void;
}

interface FAQItem {
  question: string;
  answer: string;
}

const faqData: FAQItem[] = [
  {
    question: "Does speed reading reduce comprehension?",
    answer: "Studies show comprehension is maintained up to ~500 WPM. The ORP (red letter) helps your brain lock onto words faster. Start slow and ramp up — your brain adapts naturally. Most users settle at 400-700 WPM comfortably."
  },
  {
    question: "Is RSVP reading actually effective?",
    answer: "Yes. RSVP has been peer-reviewed since 1992. The U.S. military uses it for training. Users typically see 2-3x speed increases. It's not magic — it's cognitive science. Try the 3 free stories and decide for yourself."
  },
  {
    question: "What if I read slowly now?",
    answer: "That's exactly who this is for. Flash-Read starts at 200 WPM and gradually accelerates. Your brain adapts automatically — like progressive overload for reading. Most users hit 400-700 WPM within their first week."
  },
  {
    question: "Do I need to bring my own content?",
    answer: "No! Flash-Read includes 500+ original stories across 20 genres — drama, horror, mystery, sci-fi, history, psychology, and more. You can also paste your own text or upload .txt and .epub files."
  },
  {
    question: "Is my content private?",
    answer: "Yes. Imported content is processed locally in your browser. We don't store, read, or share your uploaded files. Your reading data stays on your device."
  },
  {
    question: "How is Flash-Read different from its competitors?",
    answer: "Most RSVP readers are just the technology — you bring your own content. Flash-Read includes RSVP plus 500+ stories, gamification, achievements, streaks, daily challenges, and progress tracking. Same core tech. Way more value."
  },
  {
    question: "Can I use it on my phone?",
    answer: "Yes! Flash-Read works on any device with a web browser — phone, tablet, laptop, desktop. No app download required."
  }
];

interface AccordionItemProps {
  item: FAQItem;
  isOpen: boolean;
  onToggle: () => void;
}

const AccordionItem: React.FC<AccordionItemProps> = ({ item, isOpen, onToggle }) => {
  return (
    <div className="border-b border-slate-700/50">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-5 text-left group"
        aria-expanded={isOpen}
      >
        <span className="text-lg font-semibold text-white group-hover:text-blue-400 transition-colors">
          {item.question}
        </span>
        <ChevronDown
          className={`w-5 h-5 text-slate-400 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
          aria-hidden="true"
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-96 pb-5' : 'max-h-0'
        }`}
      >
        <p className="text-slate-400 leading-relaxed">
          {item.answer}
        </p>
      </div>
    </div>
  );
};

const LandingPage: React.FC<LandingPageProps> = ({ onStartTour }) => {
  const navigate = useNavigate();
  usePageMeta({
    title: 'Flash-Read RSVP - Speed Reading App Free | Read 3x Faster',
    description: 'Master RSVP speed reading for free. Read 3x faster with Flash-Read\'s gamified speed reading app. 500+ built-in stories, achievements, streaks. No signup required.',
  });

  // Inject inline WebPage JSON-LD structured data (FAQPage deprecated by Google as of May 2026)
  useEffect(() => {
    const existing = document.getElementById('faq-schema');
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.id = 'faq-schema';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Frequently Asked Questions - Flash-Read RSVP Speed Reading',
      description: 'Common questions about RSVP speed reading, comprehension, pricing, content privacy, and how Flash-Read works.',
      mainEntity: {
        '@type': 'ItemList',
        name: 'Frequently Asked Questions',
        itemListElement: faqData.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.answer,
            },
          },
        })),
      },
    });
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('faq-schema');
      if (el) el.remove();
    };
  }, []);

  const [userText, setUserText] = useState('');
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const handleFaqToggle = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const renderOrpWord = (word: string) => {
    const orp = calculateORP(word);
    return (
      <span className="text-2xl font-mono">
        {orp.before}<span className="text-red-500 font-bold">{orp.focus}</span>{orp.after}
      </span>
    );
  };

  return (
    <div className="flex flex-col min-h-screen pb-16">
      {/* Hero Section */}
      <section
        className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white dark:from-slate-900 dark:to-slate-950 transition-colors duration-300"
        aria-labelledby="hero-heading"
      >
        <div className="max-w-5xl mx-auto text-center">
          <h1
            id="hero-heading"
            className="text-5xl md:text-7xl font-extrabold text-slate-900 dark:text-white tracking-tight mb-8"
          >
            Read 3x faster.<br />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-500 to-pink-500">
              Actually stick with it.
            </span>
          </h1>
          <div className="stories-intro mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl border border-blue-100 dark:border-blue-800 max-w-xl mx-auto">
            <p className="text-base text-slate-700 dark:text-slate-300 leading-relaxed text-center">
              The <span className="keyword-highlight">speed reading app</span> with <span className="keyword-highlight">RSVP speed reading</span> technology.<br />
              <span className="keyword-highlight">Free speed reading</span> with <span className="keyword-highlight">gamified reading</span> experience.<br />
              Access <span className="keyword-highlight">500+ stories</span> — <span className="keyword-highlight">no signup required</span>.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {onStartTour && (
              <button
                onClick={onStartTour}
                className="group inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-primary bg-white dark:bg-slate-800 border-2 border-primary rounded-full hover:bg-blue-50 dark:hover:bg-slate-700 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-1"
                aria-label="Take a tour of Flash-Read RSVP"
              >
                <Play className="mr-2 h-5 w-5" aria-hidden="true" />
                Take the Tour
              </button>
            )}
            <button
                onClick={() => navigate('/signup')}
                className="group inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-1"
                aria-label="See pricing and get started"
              >
                <Rocket className="mr-2 h-5 w-5" aria-hidden="true" />
                See Pricing
              </button>
            <button
                onClick={() => navigate('/stories')}
                className="group inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-600 rounded-full hover:from-purple-700 hover:to-pink-700 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-1"
                aria-label="Start reading"
              >
                <BookOpen className="mr-2 h-5 w-5" aria-hidden="true" />
                Start Reading
              </button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-white dark:bg-slate-950">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-12">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-6">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <BookOpen className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">1. Pick a Story</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Choose from 500+ original, entertaining stories across 20 genres. Or import your own.
              </p>
            </div>
            <div className="p-6">
              <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Zap className="h-8 w-8 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">2. Read at Your Pace</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Words appear one at a time. Start at 200 WPM, ramp up to 2,000. Your brain adapts automatically.
              </p>
            </div>
            <div className="p-6">
              <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">3. Track Progress</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Earn XP (Experience Points), unlock achievements, build streaks. Watch your speed improve day by day.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What Is RSVP Speed Reading */}
      <section className="py-16 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950 relative" aria-labelledby="rsvp-heading">
        {/* Section accent bar */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="h-1 rounded-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 id="rsvp-heading" className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
            What Is RSVP Speed Reading?
          </h2>

          {/* Paragraph 1 — Definition (anchor) */}
          <p className="text-xl font-medium text-slate-700 dark:text-slate-300 mb-6 max-w-3xl mx-auto leading-relaxed">
            <span className="keyword-highlight">RSVP speed reading</span> (Rapid Serial Visual Presentation) is a scientifically validated reading technique where words appear one at a time at a fixed point on screen. Instead of your eyes scanning across lines, jumping between words, and backtracking, RSVP delivers each word directly to your focus point — eliminating wasted eye movement and dramatically increasing your reading speed.
          </p>

          {/* Before/After Illustration */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 max-w-3xl mx-auto">
            <div className="rsvp-illustration-card">
              <div className="w-24 h-12 bg-red-100 dark:bg-red-900/30 rounded-xl flex items-center justify-center mx-auto mb-3 overflow-hidden">
                <Eye className="w-6 h-6 text-red-500 saccade-eye" />
              </div>
              <p className="font-semibold text-slate-900 dark:text-white text-sm mb-1">Normal Reading</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Eyes scan left-to-right<br/>Jump between lines<br/>Backtrack &amp; re-read</p>
            </div>
            <div className="rsvp-illustration-card">
              <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Target className="w-6 h-6 text-emerald-500" />
              </div>
              <p className="font-semibold text-slate-900 dark:text-white text-sm mb-1">RSVP Reading</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Eyes stay fixed<br/>Words come to you<br/>Zero wasted movement</p>
            </div>
          </div>

          {/* Stat Pullquote Callout */}
          <div className="rsvp-stat-callout mb-8 max-w-lg mx-auto">
            <p className="text-4xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600 mb-1">
              2x — 3x FASTER
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Reading speed with full comprehension maintained
            </p>
          </div>

          {/* Paragraph 2 — Research/Stats (evidence) */}
          <div className="border-l-4 border-blue-400 dark:border-blue-500 pl-4 mb-6 max-w-3xl mx-auto text-left">
            <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
              Research shows RSVP readers can achieve <span className="keyword-highlight">2x to 3x faster reading speeds</span> while maintaining full comprehension. The technique works because it removes <span className="keyword-highlight">saccadic eye movements</span> — the tiny jumps your eyes make when reading normally — which account for up to 30% of reading time. With RSVP, every millisecond is spent processing words, not searching for them.
            </p>
          </div>

          {/* Paragraph 3 — Differentiation (pitch) */}
          <p className="text-lg text-slate-600 dark:text-slate-400 mb-8 max-w-3xl mx-auto leading-relaxed">
            Flash-Read's <span className="keyword-highlight">RSVP speed reading technology</span> goes further by highlighting the <span className="keyword-highlight">Optimal Recognition Point (ORP)</span> in each word — the letter your brain recognizes fastest — so you read even more efficiently. Combined with <span className="keyword-highlight">speed ramping</span> that gradually increases your <span className="keyword-highlight">words per minute (WPM)</span>, you build a lasting speed reading habit that sticks.
          </p>

          {/* Connector line */}
          <div className="rsvp-connector"></div>

          {/* Benefit Grid — Restyled with colored icons */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-left max-w-3xl mx-auto">
            <div className="rsvp-benefit-item flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-4 h-4 text-blue-500" />
              </div>
              <div>
                <p className="font-semibold text-slate-900 dark:text-white text-sm">Eliminates eye movement</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">No scanning, no line jumps, no backtracking</p>
              </div>
            </div>
            <div className="rsvp-benefit-item flex items-start gap-3">
              <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-4 h-4 text-purple-500" />
              </div>
              <div>
                <p className="font-semibold text-slate-900 dark:text-white text-sm">Proven by 30+ years of research</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Peer-reviewed studies confirm effectiveness</p>
              </div>
            </div>
            <div className="rsvp-benefit-item flex items-start gap-3">
              <div className="w-8 h-8 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
              </div>
              <div>
                <p className="font-semibold text-slate-900 dark:text-white text-sm">Adaptive speed training</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Starts at 200 WPM, ramps to 2,000 WPM</p>
              </div>
            </div>
          </div>
        </div>
        {/* Section bottom accent bar */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="h-1 rounded-full bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500"></div>
        </div>
      </section>

      {/* ORP Feature Highlight */}
      <section className="py-16 bg-gradient-to-b from-white to-slate-50 dark:from-slate-950 dark:to-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Headline */}
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-sm font-medium mb-6">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
            Science-Backed
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
            Your eyes slow you down — not your brain.
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 mb-10 max-w-2xl mx-auto">
            When you read normally, your eyes scan left-to-right, jump between lines, and backtrack (saccadic eye movements).<br />
            Flash-Read eliminates all of that.<br />
            Words appear one at a time with the <strong className="text-red-500">red letter</strong> at your brain's fastest recognition point (Optimal Recogntion Point).
          </p>

          {/* Interactive Demo — Center Stage */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 md:p-8 shadow-lg mb-10">
            <p className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-4 bg-blue-50 dark:bg-blue-900/20 px-4 py-2 rounded-lg inline-block">
              Type any word to see ORP (Optimal Reading Point) in action:
            </p>
            <input
              type="text"
              value={userText}
              onChange={(e) => setUserText(e.target.value.replace(/[^a-zA-Z]/g, ''))}
              placeholder="Try: recognition, speed, flashread..."
              className="w-full max-w-md mx-auto px-4 py-3 text-center text-xl font-mono bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-400 dark:text-white placeholder-slate-400 mb-4"
              maxLength={20}
            />
            {userText.length > 0 ? (
              <div className="text-4xl md:text-5xl font-mono font-bold">
                {renderOrpWord(userText.toLowerCase())}
              </div>
            ) : (
              <div className="text-4xl md:text-5xl font-mono font-bold text-slate-300 dark:text-slate-600">
                rec<span className="text-red-500">o</span>gnition
              </div>
            )}
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-4">
              The red letter = your brain's fastest recognition point
            </p>
          </div>

          {/* Benefits Row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-left">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-slate-900 dark:text-white text-sm">Eyes stay fixed</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">No scanning, no backtracking</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-slate-900 dark:text-white text-sm">Adaptive positioning</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">ORP shifts per word length</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-slate-900 dark:text-white text-sm">Zero setup</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Built into every session</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Read — Genre Showcase */}
      <section className="py-16 bg-white dark:bg-slate-950">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">What You'll Read</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 mb-10">
            500+ original stories. 20 genres. 3 free for guests.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {[
              { name: 'Drama', icon: Theater, color: 'text-red-500', bg: 'bg-red-100 dark:bg-red-900/30' },
              { name: 'Horror', icon: Ghost, color: 'text-purple-500', bg: 'bg-purple-100 dark:bg-purple-900/30' },
              { name: 'Mystery', icon: Search, color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/30' },
              { name: 'Sci-Fi', icon: Rocket, color: 'text-indigo-500', bg: 'bg-indigo-100 dark:bg-indigo-900/30' },
              { name: 'History', icon: History, color: 'text-amber-500', bg: 'bg-amber-100 dark:bg-amber-900/30' },
              { name: 'Psychology', icon: Brain, color: 'text-pink-500', bg: 'bg-pink-100 dark:bg-pink-900/30' },
              { name: 'Technology', icon: Cpu, color: 'text-cyan-500', bg: 'bg-cyan-100 dark:bg-cyan-900/30' },
              { name: 'Art', icon: Palette, color: 'text-violet-500', bg: 'bg-violet-100 dark:bg-violet-900/30' },
              { name: 'Philosophy', icon: MessageCircle, color: 'text-teal-500', bg: 'bg-teal-100 dark:bg-teal-900/30' },
              { name: 'Romance', icon: Heart, color: 'text-rose-500', bg: 'bg-rose-100 dark:bg-rose-900/30' },
              { name: 'Nature', icon: Mountain, color: 'text-green-500', bg: 'bg-green-100 dark:bg-green-900/30' },
              { name: 'Business', icon: Briefcase, color: 'text-slate-600 dark:text-slate-300', bg: 'bg-slate-100 dark:bg-slate-800' },
              { name: 'Travel', icon: Globe, color: 'text-sky-500', bg: 'bg-sky-100 dark:bg-sky-900/30' },
              { name: 'Health', icon: Stethoscope, color: 'text-emerald-500', bg: 'bg-emerald-100 dark:bg-emerald-900/30' },
              { name: 'Comedy', icon: Smile, color: 'text-yellow-500', bg: 'bg-yellow-100 dark:bg-yellow-900/30' },
            ].map((genre) => (
              <div key={genre.name} className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 hover:shadow-md transition-shadow group">
                <div className={`w-10 h-10 ${genre.bg} rounded-lg flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform`}>
                  <genre.icon className={`w-5 h-5 ${genre.color}`} />
                </div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{genre.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Backed by Science */}
      <section className="py-16 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">Backed by Science</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 mb-10">
            RSVP (Rapid Serial Visual Presentation) reading isn't a trend.<br />
            It's been peer-reviewed for over 30 years.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="p-6 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm">
              <p className="text-3xl font-extrabold text-blue-600 dark:text-blue-400 mb-1">286%</p>
              <p className="text-sm text-slate-600 dark:text-slate-400">Speed increase<br /><span className="text-xs text-slate-400">(Rubin & Turano, 1992)</span></p>
            </div>
            <div className="p-6 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm">
              <p className="text-3xl font-extrabold text-purple-600 dark:text-purple-400 mb-1">72%</p>
              <p className="text-sm text-slate-600 dark:text-slate-400">Peripheral vision gain<br /><span className="text-xs text-slate-400">(Yu et al, 2010)</span></p>
            </div>
            <div className="p-6 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm">
              <p className="text-3xl font-extrabold text-emerald-600 dark:text-emerald-400 mb-1">53%</p>
              <p className="text-sm text-slate-600 dark:text-slate-400">Improvement for macular degeneration</p>
            </div>
            <div className="p-6 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm">
              <p className="text-3xl font-extrabold text-orange-600 dark:text-orange-400 mb-1">1,652 WPM</p>
              <p className="text-sm text-slate-600 dark:text-slate-400">Peak rate achieved in studies</p>
            </div>
          </div>
          <button
              onClick={() => navigate('/research')}
              className="mt-8 inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline font-medium"
            >
              Read the Research →
            </button>
        </div>
      </section>

      {/* Features Grid - 5 Cards */}
      <section
        className="py-24 bg-white dark:bg-slate-950 transition-colors duration-300"
        aria-labelledby="features-heading"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="features-heading" className="sr-only">Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            
            {/* Card 1: RSVP Speed Reading */}
            <div className="p-6 rounded-2xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 transition-colors duration-300 hover:shadow-lg hover:border-blue-200 dark:hover:border-blue-700 group">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Rocket className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">RSVP Speed Reading</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                One word at a time at your optimal recognition point. Read up to 2,000 WPM — 3x faster than normal.
              </p>
            </div>

            {/* Card 2: Speed Ramp Training */}
            <div className="p-6 rounded-2xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 transition-colors duration-300 hover:shadow-lg hover:border-purple-200 dark:hover:border-purple-700 group">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Zap className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Speed Ramp Training</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                Automatically accelerate from 200 to 2,000 WPM. Most users settle at 400-700 WPM comfortably.
              </p>
            </div>

            {/* Card 3: Custom Content Import */}
            <div className="p-6 rounded-2xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 transition-colors duration-300 hover:shadow-lg hover:border-emerald-200 dark:hover:border-emerald-700 group">
              <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <BookOpen className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">500+ Stories</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                Built-in library across 20 genres. Paste any text or upload .txt and .epub files.
              </p>
            </div>

            {/* Card 4: Progress Tracking */}
            <div className="p-6 rounded-2xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 transition-colors duration-300 hover:shadow-lg hover:border-orange-200 dark:hover:border-orange-700 group">
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Gauge className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Track Progress</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                Monitor WPM improvements over time. See detailed session stats and performance charts.
              </p>
            </div>

            {/* Card 5: Achievements & Gamification */}
            <div className="p-6 rounded-2xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 transition-colors duration-300 hover:shadow-lg hover:border-pink-200 dark:hover:border-pink-700 group">
              <div className="w-12 h-12 bg-pink-100 dark:bg-pink-900/30 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Trophy className="h-6 w-6 text-pink-600 dark:text-pink-400" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Achievements</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                Earn Experience Points, unlock achievements, build streaks. Gamified reading keeps you motivated.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950" aria-labelledby="pricing-heading">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="pricing-heading" className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white text-center mb-4">
            Simple, Honest Pricing
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 text-center mb-12">
            Try free. Upgrade when you're ready. Cancel anytime. No hidden fees.
          </p>

          {/* Free Tier Banner - Horizontal above paid tiers */}
          <div className="mb-8 rounded-2xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm hover:shadow-md transition-all duration-300">
            <div className="flex flex-col sm:flex-row items-center justify-between p-6 gap-4">
              <div className="flex items-center gap-4">
                <div className="bg-gradient-to-br from-slate-500 to-slate-600 p-3 rounded-xl shadow-lg">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                 <div className="text-left">
                   <h3 className="text-xl font-bold text-slate-900 dark:text-white">Free</h3>
                   <p className="text-sm text-slate-500 dark:text-slate-400">Free tier — no credit card required</p>
                 </div>
              </div>
              <div className="flex items-center gap-6">
                <ul className="hidden md:flex flex-wrap gap-3 text-sm text-slate-600 dark:text-slate-300">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-slate-500 flex-shrink-0" />
                    3 free stories
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-slate-500 flex-shrink-0" />
                    Basic session stats
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-slate-500 flex-shrink-0" />
                    Speed badges
                  </li>
                </ul>
                <button onClick={() => navigate('/profile')} className="py-3 px-6 rounded-xl font-semibold text-white bg-slate-600 hover:bg-slate-700 transition-colors whitespace-nowrap">
                    Get Started
                </button>
              </div>
            </div>
          </div>

          {/* Paid Pricing Cards - 3 column grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Starter */}
            <div className="p-6 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col">
              <div className="mb-4">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-1">Starter</h3>
                <p className="text-3xl font-extrabold text-slate-900 dark:text-white mb-1">${TIER_PRICING.starter}<span className="text-base font-normal text-slate-500">/mo</span></p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Less than a coffee</p>
              </div>
              <ul className="space-y-3 mb-6 flex-grow">
                {TIER_BENEFITS.starter.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400"><CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />{feature}</li>
                ))}
              </ul>
              <button onClick={() => navigate('/profile')} className="w-full py-2.5 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors">
                Get Starter
              </button>
            </div>
            {/* Reader - Popular */}
            <div className="p-6 rounded-2xl bg-gradient-to-b from-blue-50 to-white dark:from-blue-900/30 dark:to-slate-800 border-2 border-blue-500 shadow-lg relative flex flex-col">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs font-semibold shadow-md">Most Popular</div>
              <div className="mb-4">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-1">Reader</h3>
                <p className="text-3xl font-extrabold text-slate-900 dark:text-white mb-1">${TIER_PRICING.reader}<span className="text-base font-normal text-slate-500">/mo</span></p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Best value</p>
              </div>
              <ul className="space-y-3 mb-6 flex-grow">
                {TIER_BENEFITS.reader.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400"><CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />{feature}</li>
                ))}
              </ul>
              <button onClick={() => navigate('/profile')} className="w-full py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md">
                Get Reader
              </button>
            </div>
            {/* Ultra */}
            <div className="p-6 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col">
              <div className="mb-4">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-1">Ultra</h3>
                <p className="text-3xl font-extrabold text-slate-900 dark:text-white mb-1">${TIER_PRICING.ultra}<span className="text-base font-normal text-slate-500">/mo</span></p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Unlock everything</p>
              </div>
              <ul className="space-y-3 mb-6 flex-grow">
                {TIER_BENEFITS.ultra.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400"><CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />{feature}</li>
                ))}
              </ul>
              <button onClick={() => navigate('/profile')} className="w-full py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 transition-all shadow-md">
                Get Ultra
              </button>
            </div>
          </div>
          <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-8">
            All plans include: RSVP reader, speed ramping, ORP highlighting, keyboard shortcuts, dark mode.
          </p>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 dark:from-purple-700 dark:via-indigo-700 dark:to-blue-700">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to read 3x faster?
          </h2>
          <p className="text-lg text-indigo-100 dark:text-indigo-200 mb-8">
            500+ stories. 20 genres. Achievements and streaks.<br />
            Try 3 free stories — no signup required.
          </p>
          <button
              onClick={() => navigate('/stories')}
              className="inline-flex items-center gap-2 px-8 py-4 text-lg font-semibold text-indigo-600 bg-white rounded-full hover:bg-indigo-50 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <BookOpen className="h-5 w-5" />
              Start Reading →
            </button>
          <p className="text-sm text-indigo-200 dark:text-indigo-300 mt-6 italic">
            "The best $5 I spend every month." — Early user
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-slate-900 dark:bg-slate-950" aria-labelledby="faq-heading">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            id="faq-heading"
            className="text-3xl md:text-4xl font-bold text-white text-center mb-12"
          >
            Questions you're probably thinking.
          </h2>
          <div className="border-t border-slate-700/50">
            {faqData.map((item, index) => (
              <AccordionItem
                key={index}
                item={item}
                isOpen={openFaqIndex === index}
                onToggle={() => handleFaqToggle(index)}
              />
            ))}
          </div>
        </div>
      </section>

    </div>
  );
};

export default LandingPage;